<template>
  <div>
    <b-card-title class="mb-2">
      {{ $t('settings.coordinates.egypt.title') }}
    </b-card-title>
    <b-card-text class="mb-3 text-muted">
      {{ $t('settings.coordinates.egypt.description') }}
    </b-card-text>

    <b-form @submit.prevent="handleSubmit">
      <b-row>
        <!-- Projection -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('settings.coordinates.egypt.projection')"
            label-for="egypt-projection"
            :description="$t('settings.coordinates.egypt.projection_desc')"
          >
            <b-form-select
              id="egypt-projection"
              v-model="form.projection"
              :options="projectionOptions"
              required
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Geographic Boundaries -->
      <h5 class="mt-3 mb-2">
        {{ $t('settings.coordinates.egypt.boundaries') }}
      </h5>
      <b-row>
        <!-- Min Latitude -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('settings.coordinates.egypt.bounds_min_lat')"
            label-for="egypt-min-lat"
            :description="$t('settings.coordinates.egypt.bounds_min_lat_desc')"
          >
            <b-form-input
              id="egypt-min-lat"
              v-model.number="form.bounds_min_lat"
              type="number"
              step="0.000001"
              min="-90"
              max="90"
              required
            />
          </b-form-group>
        </b-col>

        <!-- Max Latitude -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('settings.coordinates.egypt.bounds_max_lat')"
            label-for="egypt-max-lat"
            :description="$t('settings.coordinates.egypt.bounds_max_lat_desc')"
          >
            <b-form-input
              id="egypt-max-lat"
              v-model.number="form.bounds_max_lat"
              type="number"
              step="0.000001"
              min="-90"
              max="90"
              required
            />
          </b-form-group>
        </b-col>

        <!-- Min Longitude -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('settings.coordinates.egypt.bounds_min_lng')"
            label-for="egypt-min-lng"
            :description="$t('settings.coordinates.egypt.bounds_min_lng_desc')"
          >
            <b-form-input
              id="egypt-min-lng"
              v-model.number="form.bounds_min_lng"
              type="number"
              step="0.000001"
              min="-180"
              max="180"
              required
            />
          </b-form-group>
        </b-col>

        <!-- Max Longitude -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('settings.coordinates.egypt.bounds_max_lng')"
            label-for="egypt-max-lng"
            :description="$t('settings.coordinates.egypt.bounds_max_lng_desc')"
          >
            <b-form-input
              id="egypt-max-lng"
              v-model.number="form.bounds_max_lng"
              type="number"
              step="0.000001"
              min="-180"
              max="180"
              required
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Map Preview (Optional - can be added later) -->
      <b-alert
        show
        variant="info"
        class="mt-2"
      >
        <div class="alert-body">
          <feather-icon
            icon="MapIcon"
            class="mr-50"
          />
          <span>
            {{ $t('settings.coordinates.egypt.current_bounds') }}:
            <strong>{{ form.bounds_min_lat }}°N - {{ form.bounds_max_lat }}°N</strong>,
            <strong>{{ form.bounds_min_lng }}°E - {{ form.bounds_max_lng }}°E</strong>
          </span>
        </div>
      </b-alert>

      <!-- Actions -->
      <div class="d-flex justify-content-end mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          :disabled="loading"
          @click="resetForm"
        >
          {{ $t('common.reset') }}
        </b-button>
        <b-button
          variant="primary"
          type="submit"
          :disabled="loading"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-50"
          />
          {{ $t('common.save') }}
        </b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
import {
  BCardTitle,
  BCardText,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BAlert,
} from 'bootstrap-vue'

export default {
  name: 'EgyptSettingsForm',
  components: {
    BCardTitle,
    BCardText,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BAlert,
  },
  props: {
    settings: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        projection: 'EPSG:32636',
        bounds_min_lat: 22.0,
        bounds_max_lat: 32.0,
        bounds_min_lng: 25.0,
        bounds_max_lng: 37.0,
      },
      projectionOptions: [
        { value: 'EPSG:32636', text: 'EPSG:32636 (WGS 84 / UTM zone 36N)' },
        { value: 'EPSG:32635', text: 'EPSG:32635 (WGS 84 / UTM zone 35N)' },
        { value: 'EPSG:32637', text: 'EPSG:32637 (WGS 84 / UTM zone 37N)' },
        { value: 'EPSG:4326', text: 'EPSG:4326 (WGS 84 Geographic)' },
      ],
    }
  },
  watch: {
    settings: {
      immediate: true,
      deep: true,
      handler(newSettings) {
        if (newSettings && Object.keys(newSettings).length > 0) {
          this.form = { ...this.form, ...newSettings }
        }
      },
    },
  },
  methods: {
    handleSubmit() {
      this.$emit('save', { ...this.form })
    },
    resetForm() {
      if (this.settings && Object.keys(this.settings).length > 0) {
        this.form = { ...this.settings }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.mr-50 {
  margin-right: 0.5rem;
}
</style>
